'use client';

import SvgChevrondown from '@common/__generated__/icon/SvgChevrondown';
import { Button } from '@common/components';
import { cn } from '@core/utils';
import { useEffect, useRef, useState } from 'react';

import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';

//* 더보기 노출 기준 컨텐츠 영역 높이
const MORE_HEIGHT = 1400;

interface ContentProps {
  content: string;
  className?: string;
}

export const Content = ({ content, className }: ContentProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);

  const [isNeedMore, setIsNeedMore] = useState(false);
  const [isMore, setIsMore] = useState(false);
  const [needsOuterPadding, setNeedsOuterPadding] = useState(false);
  const [processedContent, setProcessedContent] = useState(content ?? '');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!content) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- HTML 문자열 DOM 파싱 후 가공 결과 반영 (DOMParser는 렌더 중 사용 불가)
      setProcessedContent('');

      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const body = doc.body;

    // 1. iframe만 감싸고 있는 div 삭제
    const iframes = Array.from(body.querySelectorAll('iframe'));
    iframes.forEach((iframe) => {
      const parent = iframe.parentElement;
      if (
        parent &&
        parent.tagName === 'DIV' &&
        parent.childElementCount === 1 &&
        parent.firstElementChild === iframe
      ) {
        parent.remove();
      } else {
        iframe.remove();
      }
    });

    // 2. &nbsp;만 있는 p태그를 처음 정상적인 내용 전까지 삭제
    let foundNormalContent = false;
    Array.from(body.childNodes).forEach((node) => {
      if (
        !foundNormalContent &&
        node.nodeType === Node.ELEMENT_NODE &&
        (node as HTMLElement).tagName === 'P'
      ) {
        const html = (node as HTMLElement).innerHTML.replaceAll(/\s/g, '');
        if (html === '&nbsp;') {
          body.removeChild(node);

          return;
        } else {
          foundNormalContent = true;
        }
      } else if (!foundNormalContent && node.nodeType === Node.TEXT_NODE) {
        if (!node.textContent?.trim()) {
          body.removeChild(node);
        } else {
          foundNormalContent = true;
        }
      } else {
        foundNormalContent = true;
      }
    });

    // 3. 이미지 유무 확인 및 패딩 적용
    const hasElementChildren = body.children.length > 0;
    const hasImages = body.querySelectorAll('img').length > 0;

    if (hasElementChildren) {
      Array.from(body.children).forEach((element) => {
        const hasImagesInElement = element.querySelectorAll('img').length > 0;

        if (!hasImagesInElement && element instanceof HTMLElement) {
          const existingClass = element.className;
          element.className = existingClass
            ? `${existingClass} px-20`
            : 'px-20';
        }
      });
      setNeedsOuterPadding(false);
    } else {
      setNeedsOuterPadding(!hasImages);
    }

    setProcessedContent(body.innerHTML);
  }, [content]);

  const handleMoreClick = () => {
    const currentScroll = window.scrollY;
    setIsMore(true);

    requestAnimationFrame(() => {
      window.scrollTo(0, currentScroll);
    });
  };

  useEffect(() => {
    if (isDesktopViewport) return;

    if (wrapperRef.current) {
      setIsNeedMore(wrapperRef.current.scrollHeight >= MORE_HEIGHT);
    } else {
      setIsNeedMore(false);
    }
    // eslint-disable-next-line react-hooks/refs -- wrapperRef.current?.scrollHeight를 deps로 사용해 콘텐츠 높이 변화 감지
  }, [wrapperRef.current?.scrollHeight, isDesktopViewport]);

  const isShowMoreButton = !isMore && isNeedMore;

  return (
    <div
      className={cn(
        'relative overflow-hidden border-1 border-Gray-300',
        'mobile-tablet:border-0',
        className,
      )}
      style={{
        maxHeight: !isNeedMore
          ? 'unset'
          : isMore
            ? 'unset'
            : `${MORE_HEIGHT}px`,
      }}
    >
      <div
        ref={wrapperRef}
        className={cn({ 'p-20': needsOuterPadding })}
        dangerouslySetInnerHTML={{
          __html: processedContent,
        }}
      />
      {isShowMoreButton && (
        <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-Gray-white from-[56.43%] to-transparent to-100% px-20 pb-27 pt-65">
          <Button
            theme="sub-line"
            dimensions="H48"
            onClick={handleMoreClick}
            className="w-full gap-6 font-700 text-Gray-900 typo-body3"
          >
            상세정보 더보기 <SvgChevrondown className="size-16 text-Gray-600" />
          </Button>
        </div>
      )}
    </div>
  );
};
